import React from "react";
import { Vocabulary } from "../../Utils/Vocabulary";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  isChunkLoadError: boolean;
}

// A route's lazily-loaded JS chunk can go missing after a new frontend
// deploy replaces the build output, so a user who had the app open across
// the deploy gets this instead of the component when they navigate there.
const CHUNK_LOAD_ERROR_PATTERN = /loading chunk|dynamically imported module/i;

function isChunkLoadError(error: Error): boolean {
  return error.name === "ChunkLoadError" || CHUNK_LOAD_ERROR_PATTERN.test(error.message);
}

// Catches render-time exceptions in the wrapped subtree so a single bad
// component (e.g. corrupted cached data) doesn't unmount the whole app.
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, isChunkLoadError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, isChunkLoadError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("Unhandled UI error caught by ErrorBoundary", error, errorInfo);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 32, textAlign: "center" }}>
          <p>
            {this.state.isChunkLoadError
              ? Vocabulary.appUpdatedPleaseRefresh
              : Vocabulary.unexpectedApplicationError}
          </p>
          <button onClick={this.handleReload}>{Vocabulary.reloadPage}</button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
